import React, { useEffect, useState } from "react";

import { Button, Table, Tag, Tooltip, Typography } from "antd";
import moment from "moment";

import { ReloadOutlined } from "@ant-design/icons";

import CustomSearchbar from "../../../Components/CustomSearchbar/CustomSearchbar";
import CustomSelect from "../../../Components/CustomSelect/CustomSelect";
import TableUserInfoItem from "../../../Components/TableUserInfoItem/TableUserInfoItem";
import { DATE_FORMAT, PAGE_LIMIT } from "../../../Utils/constants";
import { showAppError } from "../../../Utils/error";
import { KYCVerificationStatus } from "../../../Utils/types";
import UserAPI from "../../api/UserAPI";
import ReviewLUTModal from "./ReviewLUTModal";

const STATUS_CONTENT_MAP = {
  [KYCVerificationStatus.DETAILS_ADDED]: {
    color: "processing",
    text: "Details Added",
  },
  [KYCVerificationStatus.UNDER_REVIEW]: {
    color: "warning",
    text: "Under Review",
  },
  [KYCVerificationStatus.VERIFIED]: {
    color: "success",
    text: "Verified",
  },
  [KYCVerificationStatus.REJECTED]: {
    color: "error",
    text: "Rejected",
  },
};

const initialState = {
  loading: false,
  list: [],
  page: 1,
  limit: PAGE_LIMIT,
};

const initialFilters = {
  search: "",
  status: KYCVerificationStatus.UNDER_REVIEW,
};

/**
 * LUT (FIRC export-earnings compliance) review queue inside the Manage KYC
 * section (TMG-2249). Lists creators who have submitted an LUT for manual
 * verification, keyed off `lutVerificationStatus` independently of the main
 * KYC status.
 */
const LUTComplianceList = () => {
  const [state, setState] = useState(initialState);
  const [filters, setFilters] = useState(initialFilters);
  const [selectedLUT, setSelectedLUT] = useState(null);

  const onChangeFilters = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const fetchLUTComplianceList = async () => {
    const newState = { ...initialState, ...state };

    newState.loading = true;
    setState({ ...newState });

    try {
      const resp = await UserAPI.getUserKYCList({
        page: state.page,
        limit: state.limit,
        search: filters.search,
        // lists LUT reviews filtered by lutVerificationStatus
        lutStatus: filters.status,
        // keeps shared-platform and international creators out of the queue —
        // neither can file an LUT (TMG-3495)
        fircOnly: true,
      });

      if (resp?.code === 200) {
        newState.list = [...(resp.result || [])].map((item) => ({
          ...item,
          key: item._id,
        }));
      } else {
        showAppError(resp);
      }
    } catch (error) {
      showAppError(error);
    } finally {
      newState.loading = false;
      setState({ ...newState });
    }
  };

  const reloadList = () => {
    setState((prev) => ({ ...prev, list: [] }));
    fetchLUTComplianceList();
  };

  useEffect(() => {
    reloadList();
  }, [state.page, state.limit, filters]);

  const columns = [
    {
      title: "Submitted On",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (date) => moment(date || new Date()).format(DATE_FORMAT),
    },
    {
      title: "User Info",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <TableUserInfoItem
          name={record?.name}
          profilePic={record?.profilePicUrl}
          email={record?.email}
          phone={record?.phone}
          country={record?.country}
          dialCode={record?.dialCode}
          isCopyable
        />
      ),
    },
    {
      title: "Host",
      dataIndex: "host",
      key: "host",
    },
    {
      title: "LUT Number",
      dataIndex: "lut",
      key: "lut",
      render: (_, record) => (
        <Typography.Text copyable={Boolean(record?.lut?.number)}>
          {record?.lut?.number || "N/A"}
        </Typography.Text>
      ),
    },
    {
      title: "LUT Status",
      dataIndex: "lutVerificationStatus",
      key: "lutVerificationStatus",
      render: (lutVerificationStatus) => (
        <Tag color={STATUS_CONTENT_MAP?.[lutVerificationStatus]?.color}>
          {STATUS_CONTENT_MAP?.[lutVerificationStatus]?.text ||
            lutVerificationStatus}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Button
          className="siteBtn accentBtnV1"
          onClick={() => setSelectedLUT(record)}
          block
        >
          {record?.lutVerificationStatus === KYCVerificationStatus.UNDER_REVIEW
            ? "Review"
            : "View document"}
        </Button>
      ),
    },
  ];

  return (
    <>
      <div
        className="filters-wrapper kyc-list__filters"
        style={{
          paddingBlock: 16,
          paddingTop: 0,
        }}
      >
        <CustomSearchbar
          placeholder="Search by name, email or phone"
          searchQuery={filters.search}
          setSearchQuery={(value) => onChangeFilters("search", value)}
          style={{ marginBottom: 0, maxWidth: 500 }}
        />

        <Tooltip title="Refresh">
          <Button size="large" onClick={reloadList}>
            <ReloadOutlined />
          </Button>
        </Tooltip>

        <CustomSelect
          itemsList={Object.values(KYCVerificationStatus)
            .filter((item) => item !== KYCVerificationStatus.DETAILS_ADDED)
            .map((value) => ({
              _id: value,
              title: value.replace(/_/g, " ").toTitleCase(),
            }))}
          item={filters.status}
          placeholder="Select LUT status"
          onSelectItem={(value) => onChangeFilters("status", value)}
          hasSelectAll={false}
          style={{
            width: 250,
            marginLeft: "auto",
          }}
        />
      </div>

      <Table
        key="lutComplianceList"
        columns={columns}
        dataSource={state.loading ? [] : state.list}
        pagination={{
          current: state.page,
          showSizeChanger: false,
          pageSize: state.limit,
          onChange: (p) => setState((prev) => ({ ...prev, page: p })),
        }}
        loading={state.loading}
        rowKey={(record) => record._id}
        locale={{
          emptyText:
            (state.loading && "Loading...") ||
            ((filters.search || filters.status) &&
              "Sorry, we didn't find any matching results") ||
            "No LUT compliance submissions found",
        }}
        scroll={{ x: "auto" }}
      />

      {selectedLUT ? (
        <ReviewLUTModal
          kyc={selectedLUT}
          isModalOpen={Boolean(selectedLUT)}
          onClose={() => setSelectedLUT(null)}
          reloadList={(status) => {
            setSelectedLUT(null);
            onChangeFilters("status", status);
          }}
        />
      ) : null}
    </>
  );
};

export default LUTComplianceList;
